// Loja de Tentos (canvas T03): saldo, os 4 pacotes, o teto do mês e a compra pelo Checkout da Stripe.
import { useState } from 'react';
import type { ChavePacote } from '@truco6/planos';
import { type PacoteNaLoja, api, reais } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { voltar } from '../comum/rotas.ts';
import { CartaoSaldo, Tento, usePainelTentos } from './tentos-comum.tsx';
import { Carregando, Tela, Topo } from './comum.tsx';

const porTento = (p: PacoteNaLoja) => reais(Math.round(p.centavos / p.tentos));

export function itemDoPacote(p: PacoteNaLoja) {
  return { item_id: p.chave, item_name: `${p.tentos} Tentos`, item_category: 'tentos', price: p.centavos / 100, quantity: 1 };
}

export function LojaTentos({ sessao }: { sessao: Sessao }) {
  const { painel, erro: erroPainel, carregar } = usePainelTentos(sessao);
  const [escolhido, setEscolhido] = useState<ChavePacote>('tentos_75');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!painel) {
    return erroPainel ? (
      <Tela>
        <Topo titulo="Loja de Tentos" voltar={voltar} />
        <p className="erro" role="alert">{erroPainel}</p>
        <button className="btn2 largo" onClick={() => void carregar()}>Tentar de novo</button>
      </Tela>
    ) : <Carregando />;
  }

  const { loja } = painel;
  const liberados = loja.pacotes.filter((p) => p.liberado);
  const pacote = liberados.find((p) => p.chave === escolhido) ?? liberados.at(-1) ?? null;
  const usado = Math.min(100, Math.round((loja.gastoNoMes / loja.teto) * 100));

  async function comprar(p: PacoteNaLoja) {
    setErro(null); setOcupado(true);
    try {
      const { url } = await api<{ url: string }>(sessao, 'POST', '/tentos/comprar', { pacote: p.chave });
      medir('begin_checkout', { currency: 'BRL', value: p.centavos / 100, items: [itemDoPacote(p)] });
      location.assign(url);
    } catch (e) {
      setErro((e as Error).message);
      setOcupado(false);
      void carregar();
    }
  }

  let cta: React.ReactNode;
  if (!loja.ligada) cta = <button className="btn1 largo" disabled>A LOJA ABRE EM BREVE</button>;
  else if (!pacote) cta = <button className="btn1 largo" disabled>LIMITE DO MÊS ATINGIDO</button>;
  else cta = <button className="btn1 largo" disabled={ocupado} onClick={() => void comprar(pacote)}>COMPRAR {pacote.tentos} TENTOS · {reais(pacote.centavos)}</button>;

  return (
    <Tela>
      <Topo titulo="Loja de Tentos" voltar={voltar} />
      <div className="duas-colunas loja">
        <div className="col">
          <CartaoSaldo situacao={painel} para="extrato" />
          {!sessao.anonimo && (
            <div className="cartao barra-mes">
              <div className="linha-mes"><span>Compras neste mês</span><span className="nota">{reais(loja.gastoNoMes)} de {reais(loja.teto).replace(',00', '')}</span></div>
              <div className="trilho" role="img" aria-label={`${reais(loja.gastoNoMes)} usados do limite mensal de ${reais(loja.teto)}`}>
                <div style={{ width: `${usado}%` }} />
              </div>
            </div>
          )}
          <div className="pague-com"><span>Pague com</span><span className="chip">Cartão</span>{loja.pix && <span className="chip">Pix</span>}</div>
          {loja.pendentes.map((p) => (
            <p key={p.sessao} className="nota pendente" role="status">Pix de {p.tentos} Tentos gerado. Os Tentos entram quando ele for pago.</p>
          ))}
          <div className="notas-loja">
            <p className="centro aviso-tentos">Tentos não valem dinheiro. Não dá para sacar, trocar nem transferir.</p>
            <p className="nota centro">Desista em até 7 dias pelo extrato, se os Tentos ainda estiverem no saldo.</p>
            <p className="nota centro"><a href="/termos">Termos de uso</a></p>
          </div>
        </div>
        <div className="col">
          <div className="cab-pacotes">
            <h2 className="rotulo">Pacotes</h2>
            <span className="nota">Quanto maior, mais barato</span>
          </div>
          <div className="grade-pacotes" role="radiogroup" aria-label="Pacotes de Tentos">
            {loja.pacotes.map((p) => {
              const on = pacote?.chave === p.chave;
              return (
                <button key={p.chave} role="radio" aria-checked={on} disabled={!p.liberado || ocupado} className={'pacote' + (on ? ' on' : '')}
                  onClick={() => setEscolhido(p.chave)}>
                  {p.liberado
                    ? <span className={'selo-desconto' + (p.desconto ? '' : ' vazio') + (on ? ' cheio' : '')}>{p.desconto ? `${p.desconto}% mais barato` : ''}</span>
                    : <span className="selo-desconto limite">Limite do mês atingido</span>}
                  <span className="qtd"><Tento tamanho={22} vinco={false} /><b>{p.tentos}</b></span>
                  <span className="valor-pacote">{reais(p.centavos)}</span>
                  <span className="nota">{porTento(p)} por Tento</span>
                </button>
              );
            })}
          </div>
          {erro && <p className="erro" role="alert">{erro}</p>}
          <div className="rodape-cta">{cta}</div>
        </div>
      </div>
    </Tela>
  );
}
